import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useItems } from '../hooks/useItems';
import { itemsApi } from '../api/items';

export default function HistoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useItems({ sort: 'last_opened_at', limit: 100 });

  const handleClearHistory = async (id: string) => {
    await itemsApi.clearHistory(id);
    queryClient.invalidateQueries({ queryKey: ['items'] });
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ヘッダー */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-50"
          title="戻る"
        >
          ←
        </button>
        <h1 className="text-lg font-bold text-gray-800">閲覧履歴</h1>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {isLoading && (
          <div className="text-center text-gray-500 py-12">読み込み中...</div>
        )}

        {error && (
          <div className="text-center text-red-500 py-12">
            履歴の読み込みに失敗しました
          </div>
        )}

        {!isLoading && !error && data?.items.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            閲覧履歴はありません
          </div>
        )}

        {!isLoading && !error && data && data.items.length > 0 && (
          <div className="space-y-3">
            {data.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* サムネイル */}
                <div
                  className="flex-shrink-0 w-16 h-20 bg-gray-200 cursor-pointer"
                  onClick={() => navigate(`/manga/${item.id}`)}
                >
                  {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      No img
                    </div>
                  )}
                </div>

                {/* メタ情報 */}
                <div
                  className="flex-1 min-w-0 py-3 cursor-pointer"
                  onClick={() => navigate(`/manga/${item.id}`)}
                >
                  <div className="font-medium text-gray-900 truncate text-sm">
                    {item.title}
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">
                    {item.author}
                  </div>
                  {item.last_opened_at && (
                    <div className="text-xs text-gray-400 mt-1">
                      {formatDate(item.last_opened_at)}
                    </div>
                  )}
                </div>

                {/* 削除ボタン */}
                <button
                  onClick={() => handleClearHistory(item.id)}
                  className="flex-shrink-0 px-4 py-6 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="履歴から削除"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
